/**
 * Directory collection page — the interactive half.
 *
 * The page is split by state, not by surface (docs/directory-collection-plan.md §3). Liquid
 * renders the default view server-side and that stays crawlable — it is the whole SEO case for a
 * directory. This file takes over only once the shopper touches a control, because Liquid cannot
 * call the search index and the index is the only thing that can filter by collection membership
 * or sort by a metafield.
 *
 * The controls are one row of chips above the grid and a panel that opens under it
 * (snippets/directory-filters.liquid), never a layer over the page. The row holds All filters, the
 * shopper's picks and the chips that still lead somewhere; the panel holds every chip. A tap
 * applies at once, the chips slide to their new places, the grid dims while the index answers, then
 * the cards that stay slide to their new place and the new ones fade in.
 *
 * Browse, not search: Prefixbox treats an empty `pattern` as "everything", so the directory scopes
 * itself with `categories eq '<collection title>'` and adds the shopper's choices to that. A `*`
 * pattern returns nothing — it is not a wildcard here.
 *
 * Money never comes from the index: `SearchEngine.priceFor()` resolves each document's base price
 * against the visitor's market. See assets/search-engine.js.
 *
 * Pure helpers are on window.EwDirectory._test (tests/theme/collection-directory.test.mjs).
 */
(() => {
  const SORTS = {
    // Keys are URL-facing. `sort_by` is deliberately Shopify's own parameter name so that
    // Shopify's robots.txt rule `Disallow: /collections/*sort_by*` keeps these states out of the
    // index — they are client-rendered and must not be crawled. No rating order: every gift sits
    // between 4.5 and 5.0 stars, so it put the gifts with a single review first. An old
    // `sort_by=rating` link falls back to Featured.
    'price-descending': 'price desc',
    'price-ascending': 'price asc',
  };

  const COLLECTION_PARAM = 'filter.p.collection';
  const MIN_PARAM = 'filter.v.price.gte';
  const MAX_PARAM = 'filter.v.price.lte';
  const SORT_PARAM = 'sort_by';

  // Index prices all end in 9 (or are 50), so a band's top edge sits a cent under the next band.
  const EDGE_GAP = 0.01;
  // Rounded so the edge reads back from the URL as exactly the number written into it.
  const topOf = (to) => Math.round((to - EDGE_GAP) * 100) / 100;

  /** OData string literal. Prefixbox tolerates a raw apostrophe, but doubling is the correct form. */
  const quote = (value) => `'${String(value).replace(/'/g, "''")}'`;

  /* --------------------------------------------------------- pure helpers */

  /**
   * The index filter: the directory's own scope, one OR'd clause per row with choices, the rows
   * AND'd, then the price.
   *
   * @param {string} scope - The directory collection's title.
   * @param {Object<string, string[]>} chosen - Row → chosen collection titles.
   * @param {number|null} min - Base price, inclusive.
   * @param {number|null} max - Base price, inclusive.
   */
  const filterExpression = (scope, chosen, min, max) => {
    const parts = [];
    if (scope) parts.push(`categories eq ${quote(scope)}`);

    Object.keys(chosen).forEach((row) => {
      const values = chosen[row];
      if (!values || !values.length) return;
      parts.push(
        `(${values.map((v) => `categories eq ${quote(v)}`).join(' or ')})`,
      );
    });

    if (min !== null) parts.push(`price ge ${min}`);
    if (max !== null) parts.push(`price le ${max}`);

    return parts.join(' and ');
  };

  const inRange = (price, range) =>
    !range ||
    ((range.lo === null || price >= range.lo) &&
      (range.hi === null || price <= range.hi));

  /**
   * How many gifts a chip would leave: its own collection, inside every *other* row's choice and
   * the price range. Its own row is left out because chips in one row are OR'd, so picking a
   * second birthday-like chip widens the row rather than narrowing it.
   *
   * @param {Map<string, number>} members - Product id → price in minor units.
   * @param {Map<string, Set<string>>} rowSets - Row → ids its chosen chips cover; rows with
   *   nothing chosen are absent.
   * @param {string} row - The chip's own row.
   * @param {{lo: number|null, hi: number|null}|null} range - Minor units; null leaves price out.
   */
  const chipCount = (members, rowSets, row, range) => {
    let count = 0;

    members.forEach((price, id) => {
      for (const [other, ids] of rowSets) {
        if (other !== row && !ids.has(id)) return;
      }
      if (inRange(price, range)) count += 1;
    });

    return count;
  };

  /**
   * How many gifts a price band would leave given the rows' choices — knowable only once some row
   * has a choice, because the chips list only the members of their own collections.
   *
   * @returns {number|null} Null when no row has a choice.
   */
  const bandCount = (prices, rowSets, range) => {
    if (!rowSets.size) return null;

    let count = 0;
    prices.forEach((price, id) => {
      for (const ids of rowSets.values()) {
        if (!ids.has(id)) return;
      }
      if (inRange(price, range)) count += 1;
    });

    return count;
  };

  /**
   * A round amount between the dearest market price under a band edge and the cheapest one over
   * it, so a label never names a price the band does not hold. Null when no round amount fits.
   */
  const roundEdge = (below, above) => {
    if (below === null) return null;

    const magnitude = below >= 10000 ? 1000 : below >= 1000 ? 100 : 10;

    for (const step of [magnitude, magnitude / 2, magnitude / 10]) {
      const round = Math.ceil(below / step - 1e-9) * step;
      if (above === null || round < above) return round;
    }

    return null;
  };

  /**
   * Labels and ranges for the price bands in the visitor's currency.
   *
   * Bands are edges on the index's base price. Base to market is a lookup, not a conversion
   * (search-engine.js), so each edge is restated from the market prices of the base tiers on
   * either side of it: the label uses a round amount in that gap, and the range used for counting
   * is the exact market prices the band holds.
   *
   * @param {Array<[number, number]>} tiers - `[base, market]`, any order.
   * @param {Array<{key: string, from: number|null, to: number|null}>} bands - Base edges, `to`
   *   exclusive; null is open.
   * @param {(amount: number) => string} money - Formats an amount in the visitor's currency.
   * @param {{under: string, between: string, over: string}} labels - With `__MIN__`/`__MAX__`.
   * @returns {Array<{key: string, text: string, range: {lo: number|null, hi: number|null}}>|null}
   *   Null when a band has no tier to price it by.
   */
  const bandsInMarket = (tiers, bands, money, labels) => {
    const sorted = tiers
      .filter(([base, market]) => isFinite(base) && isFinite(market))
      .sort((a, b) => a[0] - b[0]);

    if (!sorted.length) return null;

    const below = (edge) => {
      const under = sorted.filter(([base]) => base < edge);
      return under.length ? Math.max(...under.map(([, m]) => m)) : null;
    };
    const above = (edge) => {
      const over = sorted.filter(([base]) => base >= edge);
      return over.length ? Math.min(...over.map(([, m]) => m)) : null;
    };

    const edgeLabel = (edge) => {
      const round = roundEdge(below(edge), above(edge));
      return money(round !== null ? round : above(edge));
    };

    const out = [];

    for (const band of bands) {
      const lo = band.from === null ? null : above(band.from);
      const hi = band.to === null ? null : below(band.to);

      if (
        (band.from !== null && lo === null) ||
        (band.to !== null && hi === null)
      ) {
        return null;
      }

      let text;
      if (band.from === null) {
        text = labels.under.replace('__MAX__', edgeLabel(band.to));
      } else if (band.to === null) {
        text = labels.over.replace('__MIN__', edgeLabel(band.from));
      } else {
        text = labels.between
          .replace('__MIN__', edgeLabel(band.from))
          .replace('__MAX__', edgeLabel(band.to));
      }

      out.push({
        key: band.key,
        text,
        range: {
          lo: lo === null ? null : Math.round(lo * 100),
          hi: hi === null ? null : Math.round(hi * 100),
        },
      });
    }

    return out;
  };

  /**
   * The row's suggestions: chips that still leave gifts, from groups with nothing chosen yet, the
   * largest first. Ties keep menu order, so the row doesn't reshuffle between equal counts.
   *
   * @param {Array<{key: string, row: string, count: number, index: number}>} items
   * @param {Set<string>} chosenRows - Groups with a choice; their chips are already in the row.
   * @returns {string[]} Keys, in row order.
   */
  const rankSuggestions = (items, chosenRows) =>
    items
      .filter((item) => item.count > 0 && !chosenRows.has(item.row))
      .sort((a, b) => b.count - a.count || a.index - b.index)
      .map((item) => item.key);

  /**
   * What a group's "+N more" control says. Folded, it counts the rest that still lead to gifts and
   * are out of sight; open, it offers to fold them again. Nothing to show or fold hides it.
   *
   * @param {boolean} open - The group's rest is showing.
   * @param {Array<{count: number, pressed: boolean}>} rest - The options past the main six.
   * @returns {{show: boolean, more: number}} `more` is 0 when the control reads "Fewer".
   */
  const restToggle = (open, rest) => {
    const foldable = rest.filter(
      (item) => !item.pressed && item.count > 0,
    ).length;
    if (open) return { show: foldable > 0, more: 0 };
    return { show: foldable > 0, more: foldable };
  };

  /** The product handle a card or a document links to: the key a card that stays is kept by. */
  const handleOf = (href) => {
    const match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? decodeURIComponent(match[1]) : '';
  };

  /** Drops whole minor units from a formatted amount — "$100.00" reads as "$100" on a chip. */
  const wholeMoney = (formatted) => formatted.replace(/[.,]00(?=\D*$)/, '');

  window.EwDirectory = {
    _test: {
      filterExpression,
      chipCount,
      bandCount,
      roundEdge,
      bandsInMarket,
      rankSuggestions,
      restToggle,
      handleOf,
      wholeMoney,
    },
  };

  if (window.__EW_TEST__) return;

  const start = () => {
    document.querySelectorAll('[data-directory-page]').forEach(init);
  };

  // `complete`, not `loading`: this script is deferred, so by the time it runs the document is
  // already interactive and a "still loading?" check would fire before search-engine.js has
  // defined window.SearchEngine. DOMContentLoaded is the first moment every deferred script has run.
  if (document.readyState === 'complete') start();
  else document.addEventListener('DOMContentLoaded', start);

  function init(root) {
    const engine = window.SearchEngine;

    // With no Prefixbox configuration nothing here can work. Remove the controls rather than
    // leaving dead ones on screen — the Liquid grid below is still a complete, correct page.
    if (!engine || !engine.isConfigured() || !window.ProductCardRender) {
      root
        .querySelectorAll(
          '[data-directory-filters], [data-directory-sort-wrap]',
        )
        .forEach((node) => node.remove());
      return;
    }

    // Off for the whole page: `holdInPlace` keeps the shopper's place, and the browser's own
    // anchoring would fight it, holding the cards still while the panel opens above them. Chrome
    // reads the page's setting from <body>, not from <html>; both are set.
    document.documentElement.style.overflowAnchor = 'none';
    document.body.style.overflowAnchor = 'none';

    const grid = root.querySelector('[data-directory-grid]');
    const empty = root.querySelector('[data-directory-empty]');
    const heading = root.querySelector('[data-directory-heading]');
    const status = root.querySelector('[data-directory-status]');
    const pagerWrap = root.querySelector('[data-directory-load-more-wrapper]');
    const pager = root.querySelector('[data-directory-load-more]');
    const sortSelect = root.querySelector('[data-directory-sort]');
    const sortShort = root.querySelector('[data-directory-sort-short]');
    const sortLong = root.querySelector('[data-directory-sort-long]');
    const emptyClear = root.querySelector('[data-directory-clear]');

    const row = root.querySelector('[data-directory-row]');
    const toolButton = root.querySelector('[data-directory-panel-toggle]');
    const pickCount = root.querySelector('[data-directory-pick-count]');
    const panel = root.querySelector('[data-directory-panel]');
    const panelClear = root.querySelector('[data-directory-clear-panel]');
    const panelDone = root.querySelector('[data-directory-panel-done]');
    const parts = root.querySelector('[data-directory-row-parts]');
    const part = (name) =>
      parts ? parts.content.querySelector(`[data-part="${name}"]`) : null;
    const separator = part('sep');
    const rowClear = part('clear');
    const moreButton = part('more');

    // The panel's chips are the real ones: they carry the members, and the row shows copies.
    const chips = Array.from(
      panel ? panel.querySelectorAll('[data-directory-chip]') : [],
    );
    const bandChips = Array.from(
      panel ? panel.querySelectorAll('[data-directory-band]') : [],
    );
    const groups = Array.from(
      panel ? panel.querySelectorAll('[data-directory-group]') : [],
    );
    const priceRow = root.querySelector('[data-directory-price-row]');

    const cardTemplate = document.querySelector('[data-search-card-template]');
    const starRows = document.querySelector('[data-search-star-rows]');

    if (!grid || !cardTemplate) return;

    const { attachImages, preload, firstScreenful } = window.ProductCardRender;

    const renderCard = window.ProductCardRender.create({
      cardTemplate,
      starRows,
      engine,
    });

    const collectionTitle = root.dataset.collection || '';
    const sectionId = root.dataset.sectionId || '';
    const pageSize = Number(root.dataset.pageSize) || 20;
    const headingLabel = root.dataset.headingLabel || '';
    const liquidTotal = Number(root.dataset.total) || 0;
    const idleLabel = pager ? pager.textContent.trim() : '';
    const loadingLabel = pager ? pager.dataset.loadingLabel || idleLabel : '';

    const reducedMotion = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The Liquid grid is kept, not destroyed: clearing every filter restores it verbatim rather
    // than re-querying the index for something the server already rendered correctly.
    const liquidGrid = Array.from(grid.children);
    // Not const: paging the default view appends to this snapshot, so clearing a filter later
    // restores everything the shopper had scrolled through rather than only the first page.
    let liquidPagerHidden = pagerWrap
      ? pagerWrap.classList.contains('hidden')
      : true;

    const state = {
      chosen: {},
      // The picks in the order they were made, as `c:<handle>` and `b:<band>`: the row shows them
      // in this order.
      order: [],
      band: '',
      min: null,
      max: null,
      sort: '',
      page: 1,
    };

    let total = 0;
    let loading = false;
    // Monotonic ticket for the in-flight query, so a newer one can retire an older one's result
    // instead of the older one being dropped before it is ever sent. See `run`.
    let requestId = 0;

    /* ------------------------------------------------------------ chips */

    const members = new Map();
    const prices = new Map();

    chips.forEach((chip) => {
      let list = [];
      try {
        list = JSON.parse(chip.dataset.members || '[]');
      } catch (error) {
        list = [];
      }
      const map = new Map(
        list.map(([id, price]) => [String(id), Number(price)]),
      );
      members.set(chip, map);
      map.forEach((price, id) => prices.set(id, price));
    });

    const chipByValue = (value) =>
      chips.find((candidate) => candidate.dataset.value === value) || null;
    const chipByKey = new Map(chips.map((chip) => [chip.dataset.key, chip]));
    const bandByKey = new Map(
      bandChips.map((chip) => [chip.dataset.key, chip]),
    );

    const rowOf = (value) => {
      const chip = chipByValue(value);
      return chip ? chip.dataset.group : '';
    };

    const bands = bandChips.map((chip) => ({
      key: chip.dataset.directoryBand,
      from: chip.dataset.from === '' ? null : Number(chip.dataset.from),
      to: chip.dataset.to === '' ? null : Number(chip.dataset.to),
    }));

    // Ranges in the visitor's minor units, for counting. US prices are the index's own; elsewhere
    // they wait for the price tiers, and until then a band does not narrow the chips' counts.
    let bandRanges = new Map();
    // The bands join the row only once their labels are in the visitor's currency.
    let bandsReady = !!priceRow && priceRow.dataset.usPrices === 'true';
    if (bandsReady) {
      bands.forEach((band) => {
        bandRanges.set(band.key, {
          lo: band.from === null ? null : Math.round(band.from * 100),
          hi: band.to === null ? null : Math.round(topOf(band.to) * 100),
        });
      });
    }

    const bandFor = (key) => bands.find((band) => band.key === key) || null;

    const chosenCount = () =>
      Object.values(state.chosen).reduce(
        (sum, values) => sum + values.length,
        0,
      );

    const hasFilter = () =>
      chosenCount() > 0 || state.min !== null || state.max !== null;

    const isFiltered = () => hasFilter() || state.sort !== '';

    const rowSets = () => {
      const sets = new Map();

      Object.keys(state.chosen).forEach((row) => {
        const values = state.chosen[row];
        if (!values.length) return;

        const ids = new Set();
        chips.forEach((chip) => {
          if (
            chip.dataset.group === row &&
            values.includes(chip.dataset.value)
          ) {
            members.get(chip).forEach((price, id) => ids.add(id));
          }
        });
        sets.set(row, ids);
      });

      return sets;
    };

    const activeRange = () =>
      state.band ? bandRanges.get(state.band) || null : null;

    const desktop = window.matchMedia('(min-width: 750px)');
    // On a phone the row scrolls sideways; past this many suggestions the rest wait in the panel.
    const PHONE_SUGGESTIONS = 12;
    const counts = new Map();
    const rowCopies = new Map();
    let rowSignature = '';

    /** The row's copy of a panel chip, made once and kept, so it can slide rather than reappear. */
    const copyOf = (key) => {
      let copy = rowCopies.get(key);
      if (!copy) {
        const source = chipByKey.get(key) || bandByKey.get(key);
        if (!source) return null;
        copy = source.cloneNode(true);
        copy.removeAttribute('data-members');
        rowCopies.set(key, copy);
      }
      return copy;
    };

    // The row Liquid printed is the unpicked order; its chips become the first copies.
    if (row) {
      row
        .querySelectorAll('[data-directory-chip], [data-directory-band]')
        .forEach((node) => rowCopies.set(node.dataset.key, node));
    }

    const syncCopy = (copy) => {
      const source =
        chipByKey.get(copy.dataset.key) || bandByKey.get(copy.dataset.key);
      if (!source) return;
      copy.setAttribute('aria-pressed', source.getAttribute('aria-pressed'));
      // The text only: rewriting the whole chip would re-create its photo, and it would blink.
      ['[data-directory-chip-count]', '[data-directory-band-label]'].forEach(
        (selector) => {
          const from = source.querySelector(selector);
          const to = copy.querySelector(selector);
          if (from && to) to.textContent = from.textContent;
        },
      );
    };

    /**
     * On desktop the row is one line: trailing suggestions give way to "+N more" until it fits.
     * Picks never give way; if they alone overflow, the row wraps rather than hiding one.
     */
    // While the panel is open the row's link closes it again, so it says so.
    const moreText = (count) =>
      panel && !panel.hidden
        ? moreButton.dataset.labelFewer || ''
        : (moreButton.dataset.label || '').replace('__COUNT__', count);

    const fitRow = () => {
      if (!row || !desktop.matches) return;

      const suggested = Array.from(row.querySelectorAll('[data-suggested]'));
      suggested.forEach((node) => {
        node.hidden = false;
      });
      row.style.flexWrap = '';
      if (moreButton) moreButton.hidden = true;
      if (row.scrollWidth <= row.clientWidth + 1) return;

      // The link's text changes width as the count grows and between "+N more" and "Fewer", so it is
      // rewritten before every measurement; set only at the end, the row could fit with one text
      // and overflow with the other.
      if (moreButton) {
        moreButton.hidden = false;
        moreButton.textContent = moreText(0);
      }
      let hidden = 0;
      for (
        let index = suggested.length - 1;
        index >= 0 && row.scrollWidth > row.clientWidth + 1;
        index -= 1
      ) {
        suggested[index].hidden = true;
        hidden += 1;
        if (moreButton) moreButton.textContent = moreText(hidden);
      }
      if (row.scrollWidth > row.clientWidth + 1) row.style.flexWrap = 'wrap';
    };

    const renderRow = () => {
      if (!row || !toolButton) return;

      const picks = state.order.map(copyOf).filter(Boolean);
      const chosenRows = new Set(Object.keys(state.chosen));
      const ranked = rankSuggestions(
        chips.map((chip, index) => ({
          key: chip.dataset.key,
          row: chip.dataset.group,
          count: counts.get(chip) || 0,
          index,
        })),
        chosenRows,
      );
      if (!state.band && bandsReady) {
        bandChips.forEach((chip) => {
          const count = counts.get(chip);
          if (count === null || count > 0) ranked.push(chip.dataset.key);
        });
      }

      const wide = desktop.matches;
      const shown = (wide ? ranked : ranked.slice(0, PHONE_SUGGESTIONS))
        .map(copyOf)
        .filter(Boolean);
      const extra = ranked.length - shown.length;

      const nodes = [toolButton, ...picks];
      if (picks.length > 1 && rowClear) nodes.push(rowClear);
      if (picks.length && shown.length && separator) nodes.push(separator);
      nodes.push(...shown);
      if (moreButton && (wide || extra > 0)) nodes.push(moreButton);

      const focused = row.contains(document.activeElement)
        ? document.activeElement
        : null;
      row.replaceChildren(...nodes);

      picks.forEach((copy) => {
        syncCopy(copy);
        copy.hidden = false;
        delete copy.dataset.suggested;
      });
      shown.forEach((copy) => {
        syncCopy(copy);
        copy.hidden = false;
        copy.dataset.suggested = '';
      });

      if (moreButton && !wide) {
        moreButton.hidden = false;
        moreButton.textContent = moreText(extra);
      }
      fitRow();

      // A new pick leads the row, so a phone's row scrolls back to show it.
      const signature = state.order.join('|');
      if (signature !== rowSignature && !wide) row.scrollLeft = 0;
      rowSignature = signature;

      if (focused && focused.isConnected && !focused.hidden) {
        focused.focus({ preventScroll: true });
      } else if (focused) {
        toolButton.focus({ preventScroll: true });
      }
    };

    // Groups whose options past the main six are showing; folded is the default.
    const openGroups = new Set();

    const syncControls = () => {
      const sets = rowSets();
      const range = activeRange();
      const anyPick = state.order.length > 0;

      chips.forEach((chip) => {
        const { group, value } = chip.dataset;
        const pressed = (state.chosen[group] || []).includes(value);
        const count = chipCount(members.get(chip), sets, group, range);
        counts.set(chip, count);

        chip.setAttribute('aria-pressed', String(pressed));
        // A chosen chip always shows: it is the way to take that filter off.
        const folded =
          chip.hasAttribute('data-directory-rest') &&
          !openGroups.has(chip.closest('[data-directory-group]'));
        chip.hidden = !pressed && (count === 0 || folded);

        const badge = chip.querySelector('[data-directory-chip-count]');
        if (badge) badge.textContent = count;
      });

      bandChips.forEach((chip) => {
        const key = chip.dataset.directoryBand;
        const pressed = key === state.band;
        const count = bandCount(prices, sets, bandRanges.get(key) || null);
        counts.set(chip, count);

        chip.setAttribute('aria-pressed', String(pressed));
        chip.hidden = !pressed && count === 0;
      });

      // Says how many chips a pick hid, so a short group doesn't read as the whole range. Folded
      // ones are not counted: "+N more" speaks for those.
      const some = panel ? panel.dataset.noteSome || '' : '';
      const all = panel ? panel.dataset.noteAll || '' : '';
      const moreLabel = panel ? panel.dataset.labelMore || '' : '';
      const fewerLabel = panel ? panel.dataset.labelFewer || '' : '';
      groups.forEach((group) => {
        const options = Array.from(
          group.querySelectorAll(
            '[data-directory-chip], [data-directory-band]',
          ),
        );
        const pressedOf = (node) =>
          node.getAttribute('aria-pressed') === 'true';

        const note = group.querySelector('[data-directory-group-note]');
        if (note) {
          const dead = options.filter(
            (node) => !pressedOf(node) && counts.get(node) === 0,
          ).length;
          note.hidden = !anyPick || dead === 0;
          note.textContent =
            dead === options.length ? all : some.replace('__COUNT__', dead);
        }

        const toggle = group.querySelector('[data-directory-group-more]');
        if (toggle) {
          const open = openGroups.has(group);
          const { show, more } = restToggle(
            open,
            options
              .filter((node) => node.hasAttribute('data-directory-rest'))
              .map((node) => ({
                count: counts.get(node) || 0,
                pressed: pressedOf(node),
              })),
          );
          toggle.hidden = !show;
          toggle.setAttribute('aria-expanded', String(open));
          toggle.textContent = open
            ? fewerLabel
            : moreLabel.replace('__COUNT__', more);
        }
      });

      if (pickCount) {
        pickCount.hidden = !anyPick;
        pickCount.textContent = state.order.length;
      }
      if (panelClear) panelClear.style.visibility = hasFilter() ? '' : 'hidden';

      renderRow();
    };

    /* ----------------------------------------------------------- motion */

    /** Where each chip on screen is now, so the next draw can slide it from there. */
    const chipPlaces = () => {
      const places = new Map();
      if (reducedMotion()) return places;
      root
        .querySelectorAll(
          '[data-directory-row] > *, [data-directory-panel]:not([hidden]) [data-directory-chip], [data-directory-panel]:not([hidden]) [data-directory-band]',
        )
        .forEach((node) => {
          if (!node.hidden) places.set(node, node.getBoundingClientRect());
        });
      return places;
    };

    const slideChips = (before) => {
      if (reducedMotion()) return;

      const nodes = root.querySelectorAll(
        '[data-directory-row] > *, [data-directory-panel]:not([hidden]) [data-directory-chip], [data-directory-panel]:not([hidden]) [data-directory-band]',
      );
      nodes.forEach((node) => {
        if (node.hidden || typeof node.animate !== 'function') return;
        const was = before.get(node);
        if (!was) {
          node.animate(
            [
              { opacity: 0, transform: 'scale(0.92)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 220, easing: 'ease-out' },
          );
          return;
        }
        const now = node.getBoundingClientRect();
        const dx = was.left - now.left;
        const dy = was.top - now.top;
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
        node.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
          { duration: 300, easing: 'cubic-bezier(0.22, 0.8, 0.3, 1)' },
        );
      });
    };

    /* ------------------------------------------------------------ view */

    const setHeading = (count) => {
      if (heading && headingLabel) {
        heading.textContent = headingLabel.replace('__COUNT__', count);
      }
      if (panelDone) {
        panelDone.textContent =
          count === 1
            ? panelDone.dataset.labelOne || ''
            : (panelDone.dataset.labelOther || '').replace('__COUNT__', count);
      }
      if (status && status.dataset.label) {
        status.textContent = status.dataset.label.replace('__COUNT__', count);
      }
    };

    const setBusy = (on) => {
      if (on) grid.setAttribute('aria-busy', 'true');
      else grid.removeAttribute('aria-busy');
    };

    const animateIn = (nodes) => {
      if (reducedMotion()) return;

      nodes.forEach((node, index) => {
        if (typeof node.animate !== 'function') return;
        node.animate(
          [
            { opacity: 0, transform: 'translateY(12px)' },
            { opacity: 1, transform: 'none' },
          ],
          {
            duration: 320,
            // Capped, so the twentieth card does not wait half a second for its turn.
            delay: Math.min(index, 8) * 35,
            easing: 'cubic-bezier(0.22, 0.8, 0.3, 1)',
            fill: 'backwards',
          },
        );
      });
    };

    /**
     * Puts `nodes` in the grid in that order. Cards present before and after slide from their old
     * place to the new one; the rest fade in.
     */
    const swapGrid = (nodes) => {
      const animate = !reducedMotion();
      const before = new Map();

      if (animate) {
        Array.from(grid.children).forEach((node) => {
          before.set(node, node.getBoundingClientRect());
        });
      }

      grid.replaceChildren(...nodes);
      attachImages(grid);

      if (!animate) return;

      const fresh = [];
      nodes.forEach((node) => {
        const was = before.get(node);
        if (!was) {
          fresh.push(node);
          return;
        }
        const now = node.getBoundingClientRect();
        const dx = was.left - now.left;
        const dy = was.top - now.top;
        if ((dx || dy) && typeof node.animate === 'function') {
          node.animate(
            [
              { transform: `translate(${dx}px, ${dy}px)` },
              { transform: 'none' },
            ],
            { duration: 320, easing: 'cubic-bezier(0.22, 0.8, 0.3, 1)' },
          );
        }
      });

      // Only what can be seen gets a stagger; cards further down arrive as the shopper scrolls.
      const viewport = window.innerHeight;
      animateIn(
        fresh.filter((node) => node.getBoundingClientRect().top < viewport),
      );
    };

    const restoreLiquid = () => {
      setBusy(false);
      swapGrid(liquidGrid);
      grid.style.display = '';
      if (empty) empty.style.display = 'none';
      if (pagerWrap) {
        pagerWrap.style.display = '';
        pagerWrap.classList.toggle('hidden', liquidPagerHidden);
      }
      setHeading(liquidTotal);
    };

    const paint = (documents, append) => {
      if (append) {
        const added = documents.map(renderCard);
        grid.append(...added);
        attachImages(grid);
        animateIn(added);
      } else {
        // A card the shopper can already see keeps its node, and so its loaded image.
        const reuse = new Map();
        Array.from(grid.children).forEach((node) => {
          const link = node.querySelector('a[href*="/products/"]');
          const handle = link ? handleOf(link.getAttribute('href')) : '';
          if (handle && !reuse.has(handle)) reuse.set(handle, node);
        });

        const nodes = documents.map((doc) => {
          const handle = handleOf(doc.url);
          const node = reuse.get(handle);
          if (node) {
            reuse.delete(handle);
            return node;
          }
          return renderCard(doc);
        });

        swapGrid(nodes);
      }

      const shown = grid.children.length;
      const hasMore = shown < total;

      if (pagerWrap) {
        pagerWrap.style.display = '';
        pagerWrap.classList.toggle('hidden', !hasMore);
      }

      const nothing = shown === 0;
      grid.style.display = nothing ? 'none' : '';
      if (empty) empty.style.display = nothing ? 'flex' : 'none';
    };

    /* ------------------------------------------------------------- URL */

    const syncUrl = () => {
      const url = new URL(window.location.href);
      const params = url.searchParams;

      // `filter.p.group` came from the dropdown this replaced; dropped so an old link tidies up.
      [
        COLLECTION_PARAM,
        'filter.p.group',
        MIN_PARAM,
        MAX_PARAM,
        SORT_PARAM,
      ].forEach((p) => params.delete(p));

      // One param per value rather than a comma-joined list: these values are collection *titles*,
      // and a title may contain a comma ("Food, Drink & More"), which a joined list cannot round
      // trip. Repeated params are also the shape of Shopify's own faceted filter URLs.
      Object.values(state.chosen).forEach((values) =>
        values.forEach((value) => params.append(COLLECTION_PARAM, value)),
      );
      if (state.min !== null) params.set(MIN_PARAM, state.min);
      if (state.max !== null) params.set(MAX_PARAM, state.max);
      if (state.sort) params.set(SORT_PARAM, state.sort);

      window.history.replaceState({}, '', url.toString());

      // A single filter param is deliberately left crawlable by Shopify's robots.txt, on the
      // assumption that filtered collection views are server-rendered. Ours are not, so every
      // filtered state points its canonical at the bare collection URL.
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `${url.origin}${url.pathname}`;
    };

    const readUrl = () => {
      const params = new URLSearchParams(window.location.search);

      params
        .getAll(COLLECTION_PARAM)
        .filter(Boolean)
        .forEach((value) => {
          const chip = chipByValue(value);
          if (!chip) return;
          const group = chip.dataset.group;
          state.chosen[group] = state.chosen[group] || [];
          if (state.chosen[group].includes(value)) return;
          state.chosen[group].push(value);
          state.order.push(chip.dataset.key);
        });

      const min = params.get(MIN_PARAM);
      const max = params.get(MAX_PARAM);
      if (min !== null && min !== '') state.min = Number(min);
      if (max !== null && max !== '') state.max = Number(max);

      // A range from a band reads back as that band; any other range still filters, it just has
      // no chip to show it.
      const band = bands.find(
        (candidate) =>
          (candidate.from === null ? null : candidate.from) === state.min &&
          (candidate.to === null ? null : topOf(candidate.to)) === state.max,
      );
      if (band) {
        state.band = band.key;
        state.order.push(`b:${band.key}`);
      }

      const sort = params.get(SORT_PARAM);
      if (sort && SORTS[sort]) state.sort = sort;
    };

    /* ----------------------------------------------------------- fetch */

    /**
     * One query against the index. `append` adds the next page to the grid; otherwise it replaces
     * it.
     *
     * A filter change *supersedes* whatever is in flight rather than being discarded: the shopper
     * can tap a second chip while the first query is still out, and the last choice is the one
     * that lands. Only load-more refuses to re-enter, because its page counter must not advance
     * past a request that has not come back yet.
     *
     * @param {boolean} append - Keep the current cards and add the next page after them.
     */
    const run = async (append) => {
      if (append && loading) return;

      const token = ++requestId;
      const stale = () => token !== requestId;

      loading = true;
      // Inside the guard, not at the call site: advancing it before the re-entry check let a
      // second load-more click skip a page outright.
      state.page = append ? state.page + 1 : 1;

      if (!append) {
        setBusy(true);
      } else if (pager) {
        pager.textContent = loadingLabel;
        pager.setAttribute('aria-busy', 'true');
      }

      try {
        const result = await engine.search('', {
          page: state.page,
          pageSize,
          sort: state.sort ? SORTS[state.sort] : '',
          filter: filterExpression(
            collectionTitle,
            state.chosen,
            state.min,
            state.max,
          ),
        });

        if (stale()) return;

        // Money is not in the index, so the tier map has to be filled before the cards are built —
        // `renderCard` reads it synchronously and a miss paints a card with no price and no
        // savings badge. A price failure is not a search failure: it degrades to cards without
        // money rather than throwing the whole result away.
        try {
          await engine.resolvePrices(result.documents);
        } catch (error) {
          console.warn('[directory] prices unavailable', error);
        }

        if (stale()) return;

        // The first screenful of images, fetched while the old grid is still up and dimmed, so
        // the new cards arrive with their pictures. Short, because the shopper is waiting on a tap.
        if (!append) {
          await preload(result.documents, {
            count: firstScreenful(grid),
            timeout: 700,
          });
          if (stale()) return;
        }

        // No `result.sprawl` check: every query this page makes is already scoped by
        // `categories eq '<collection>'`, so unlike a bare search term it never asks the index for
        // everything, and the flag cannot tell a large collection from a runaway one.
        total = result.empty ? 0 : result.total;
        setBusy(false);
        setHeading(total);
        paint(result.documents, append);
      } catch (error) {
        if (stale()) return;

        console.error('[directory]', error);

        // A failed append keeps what is on screen — those results are still correct, only the
        // extra page is missing — and rolls the counter back so the next click retries the same
        // page. Only a failed first page falls back to the server-rendered grid.
        if (append) state.page -= 1;
        else restoreLiquid();
      } finally {
        // A superseded request leaves the flag alone: the query that replaced it owns it now.
        if (!stale()) {
          loading = false;
          setBusy(false);
          if (pager) {
            pager.textContent = idleLabel;
            pager.removeAttribute('aria-busy');
          }
        }
      }
    };

    /**
     * Holds `node` at its height on screen across a redraw, so a chip tapped in the panel stays
     * under the finger while the chips around it come and go and the panel shrinks or grows.
     * Safari doesn't do this by itself, and Chrome's own scroll anchoring locks onto cards the grid
     * is swapping and jumps the other way, so the page does it for both (anchoring is off, below).
     *
     * @param {Element|null} node
     * @returns {() => void} Call once the redraw is in the DOM.
     */
    const holdInPlace = (node) => {
      if (!node) return () => {};

      // A chip can hide itself by being tapped; its group then holds the place instead.
      const group = node.closest('[data-directory-group]');
      const top = node.getBoundingClientRect().top;
      const groupTop = group ? group.getBoundingClientRect().top : 0;

      return () => {
        let shift = 0;
        if (node.getClientRects().length) {
          shift = node.getBoundingClientRect().top - top;
        } else if (group && group.getClientRects().length) {
          shift = group.getBoundingClientRect().top - groupTop;
        }
        if (Math.abs(shift) >= 1) {
          window.scrollBy({ top: shift, behavior: 'instant' });
        }
      };
    };

    const refresh = (hold) => {
      const before = chipPlaces();
      syncUrl();
      syncControls();
      if (hold) hold();
      slideChips(before);
      if (isFiltered()) {
        run(false);
      } else {
        // Retires a query still in flight, which would otherwise land over the restored grid.
        requestId += 1;
        loading = false;
        restoreLiquid();
      }
    };

    const track = (level, item, action, place) => {
      if (!window.EwTrack || typeof window.EwTrack.push !== 'function') return;
      window.EwTrack.push('ew_directory_filter', {
        level,
        item,
        action,
        place,
        device: window.EwTrack.device ? window.EwTrack.device() : undefined,
      });
    };

    /* --------------------------------------------------------- controls */

    const placeOf = (node) => {
      if (panel && panel.contains(node)) return 'panel';
      return node.getAttribute('aria-pressed') === 'true' ? 'pick' : 'row';
    };

    const toggleChip = (chip, place, hold) => {
      const { group, value, handle, key } = chip.dataset;
      const values = state.chosen[group] || [];
      const on = !values.includes(value);

      state.chosen[group] = on
        ? [...values, value]
        : values.filter((candidate) => candidate !== value);
      if (!state.chosen[group].length) delete state.chosen[group];
      state.order = state.order.filter((candidate) => candidate !== key);
      if (on) state.order.push(key);

      track(group, handle, on ? 'add' : 'remove', place);
      refresh(hold);
    };

    const toggleBand = (key, place, hold) => {
      const on = state.band !== key;
      const band = on ? bandFor(key) : null;

      state.band = on ? key : '';
      state.min = band && band.from !== null ? band.from : null;
      state.max = band && band.to !== null ? topOf(band.to) : null;
      state.order = state.order.filter((candidate) => candidate[0] !== 'b');
      if (on) state.order.push(`b:${key}`);

      track('price', key, on ? 'add' : 'remove', place);
      refresh(hold);
    };

    const clearFilters = (place, hold) => {
      // Filters only: the order the shopper picked is not a filter, and it stays.
      state.chosen = {};
      state.order = [];
      state.band = '';
      state.min = null;
      state.max = null;
      track('all', '', 'clear', place);
      refresh(hold);
    };

    /**
     * Puts the row just under the header, with the gifts after it. The row's scroll margin
     * (snippets/directory-filters.liquid) clears the header at its full height, which it grows
     * back to while the page scrolls up.
     */
    const bringRowIntoView = () => {
      if (!row) return;
      row.scrollIntoView({
        block: 'start',
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    };

    // What the sticky header covers right now, published by SlidingHeader (assets/header.js).
    const headerCover = () =>
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-height',
        ),
      ) || 0;

    const openPanel = (open, place) => {
      if (!panel || !toolButton || panel.hidden === !open) return;

      panel.hidden = !open;
      toolButton.setAttribute('aria-expanded', String(open));
      if (moreButton) moreButton.setAttribute('aria-expanded', String(open));
      renderRow();
      if (!open) return;

      track('panel', '', 'open', place);

      // Under the header the button is out of sight, and low on the screen the panel would open
      // out of it; either way the row moves up to where both show.
      const top = toolButton.getBoundingClientRect().top;
      if (top < headerCover() || top > window.innerHeight * 0.55) {
        bringRowIntoView();
      }

      if (!reducedMotion() && typeof panel.animate === 'function') {
        panel.animate(
          [
            { opacity: 0, transform: 'translateY(-6px)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 260, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' },
        );
      }
    };

    root.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-directory-chip]');
      if (chip) {
        const source = chipByKey.get(chip.dataset.key);
        if (source) toggleChip(source, placeOf(chip), holdInPlace(chip));
        return;
      }

      const band = event.target.closest('[data-directory-band]');
      if (band) {
        toggleBand(
          band.dataset.directoryBand,
          placeOf(band),
          holdInPlace(band),
        );
        return;
      }

      if (event.target.closest('[data-directory-panel-toggle]')) {
        openPanel(panel ? panel.hidden : false, 'button');
      } else if (event.target.closest('[data-directory-more]')) {
        openPanel(panel ? panel.hidden : false, 'more');
      } else if (event.target.closest('[data-directory-clear-row]')) {
        clearFilters('row', holdInPlace(row));
      } else if (event.target.closest('[data-directory-group-more]')) {
        const toggle = event.target.closest('[data-directory-group-more]');
        const group = toggle.closest('[data-directory-group]');
        const open = !openGroups.has(group);
        if (open) openGroups.add(group);
        else openGroups.delete(group);

        // Nothing above the group changes size, so the page stays still. Holding the link itself
        // in place would scroll the page by the group's change in height, since the link moves to
        // the end of whatever opens.
        const before = chipPlaces();
        syncControls();
        slideChips(before);
        track(
          group.dataset.group || '',
          '',
          open ? 'expand' : 'collapse',
          'panel',
        );
      }
    });

    if (panelClear) {
      // The button sits in the panel's sticky bar, so the group at the top of the screen holds the
      // place while every chip comes back.
      panelClear.addEventListener('click', () => {
        const inView = groups.find(
          (group) => group.getBoundingClientRect().bottom > 0,
        );
        clearFilters('panel', holdInPlace(inView || null));
      });
    }
    if (emptyClear) {
      emptyClear.addEventListener('click', () => clearFilters('empty'));
    }

    if (panelDone) {
      panelDone.addEventListener('click', () => {
        openPanel(false);
        // "Show N gifts" is a promise to show them: the row, with its picks, and the gifts under
        // it, wherever in the panel the shopper was.
        bringRowIntoView();
        if (toolButton) toolButton.focus({ preventScroll: true });
      });
    }

    const syncSortLabel = () => {
      if (!sortSelect) return;
      const option = sortSelect.options[sortSelect.selectedIndex];
      if (!option) return;
      if (sortLong) sortLong.textContent = option.textContent.trim();
      if (sortShort) {
        sortShort.textContent =
          option.dataset.short || option.textContent.trim();
      }
    };

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        state.sort = SORTS[sortSelect.value] ? sortSelect.value : '';
        syncSortLabel();
        track('sort', state.sort || 'featured', 'set', 'sort');
        refresh();
      });
    }

    desktop.addEventListener('change', renderRow);
    if (row && typeof ResizeObserver === 'function') {
      // Width only, and a frame later: fitting can change the row's height, and refitting inside
      // the observer's own callback is the loop browsers report as an error.
      let fittedWidth = 0;
      new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === fittedWidth) return;
        fittedWidth = width;
        window.requestAnimationFrame(fitRow);
      }).observe(row);
    }

    /* ------------------------------------------------- price labels */

    const relabelBands = () => {
      if (!priceRow || typeof engine.marketPrices !== 'function') return false;

      const labelled = bandsInMarket(
        engine.marketPrices(),
        bands,
        (amount) => wholeMoney(engine.formatMoney(amount)),
        {
          under: priceRow.dataset.labelUnder || '',
          between: priceRow.dataset.labelBetween || '',
          over: priceRow.dataset.labelOver || '',
        },
      );
      if (!labelled) return false;

      bandRanges = new Map(labelled.map((band) => [band.key, band.range]));
      labelled.forEach((band) => {
        const chip = bandChips.find(
          (candidate) => candidate.dataset.directoryBand === band.key,
        );
        const label = chip && chip.querySelector('[data-directory-band-label]');
        if (label) label.textContent = band.text;
      });

      priceRow.style.visibility = '';
      bandsReady = true;
      return true;
    };

    /**
     * Appends the next page of the **default** view, fetched from Liquid through the Section
     * Rendering API.
     *
     * It cannot come from the index. The unfiltered grid is in the collection's manual sort order
     * and the index has its own — on `experience-gifts` the two share not one product across the
     * whole first page — so appending the index's page 2 to Liquid's page 1 would repeat twenty
     * products and skip twenty others. Paging the server-rendered view has to go back to the
     * server that rendered it.
     */
    const loadLiquidPage = async () => {
      if (loading || !pager) return;
      loading = true;

      pager.textContent = loadingLabel;
      pager.setAttribute('aria-busy', 'true');

      const href = pager.getAttribute('href');
      const separator = href.includes('?') ? '&' : '?';

      try {
        const response = await fetch(
          `${href}${separator}section_id=${sectionId}`,
        );

        if (!response.ok) {
          throw new Error(`Directory page request failed: ${response.status}`);
        }

        const next = new DOMParser().parseFromString(
          await response.text(),
          'text/html',
        );
        const nextGrid = next.querySelector('[data-directory-grid]');
        const nextPager = next.querySelector('[data-directory-load-more]');
        const nextHref = nextPager && nextPager.getAttribute('href');

        // A filter applied while this was in flight hands the grid and the pager to the index.
        // The fetched cards still belong in the restore snapshot — they are the next page of the
        // default view either way — but they must not be dropped in under someone else's results.
        const ours = !isFiltered();

        if (nextGrid) {
          const added = Array.from(nextGrid.children);
          liquidGrid.push(...added);
          if (ours) {
            grid.append(...added);
            animateIn(added);
          }
        }

        // Liquid gives the anchor a real href only while a further page exists, falling back to
        // '#'. Either absence means this was the last one.
        //
        // The href advances even when the index has taken over: nothing on the filtered path
        // reads it, and leaving it on the page just appended would re-append that same page the
        // next time the shopper clears the filter and pages on. Only the visibility class is
        // withheld, because `paint` owns it while a filter is active.
        if (nextHref && nextHref !== '#') {
          pager.setAttribute('href', nextHref);
        } else {
          liquidPagerHidden = true;
          if (ours && pagerWrap) pagerWrap.classList.add('hidden');
        }
      } catch (error) {
        console.error('[directory]', error);
        // A failed append still has a working page URL behind it — follow it rather than leaving
        // the shopper on a dead button.
        window.location.href = href;
      } finally {
        loading = false;
        pager.textContent = idleLabel;
        pager.removeAttribute('aria-busy');
      }
    };

    if (pager) {
      pager.addEventListener('click', (event) => {
        if (isFiltered()) {
          event.preventDefault();
          // The page counter advances inside `run`, after its re-entry guard — advancing it here
          // let a second click while page N was in flight skip straight past page N + 1.
          run(true);
          return;
        }

        // Unfiltered and with no section to re-render, it stays an ordinary link to page 2: a
        // full page load, but never a dead button.
        if (!sectionId) return;

        event.preventDefault();
        loadLiquidPage();
      });
    }

    /* ------------------------------------------------------- start-up */

    readUrl();
    if (sortSelect) sortSelect.value = state.sort;
    syncSortLabel();
    syncControls();

    // Fills the price tiers, which both the cards' money and the band labels outside the US are
    // read from. Fire and forget — a failure only means prices resolve on demand.
    const warming =
      typeof engine.warm === 'function' ? engine.warm() : Promise.resolve();
    Promise.resolve(warming).then(() => {
      if (relabelBands()) syncControls();
    });

    if (isFiltered()) run(false);
  }
})();
